<script setup>
import { computed, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { spDateKey } from '../agendaDateUtils';
import { meetingOpacityClass } from '../agendaStatus';
import { useMeetingDayDrag } from '../composables/useMeetingDayDrag';

const props = defineProps({
  currentDate: { type: Date, required: true },
  meetings: { type: Array, default: () => [] },
});

const emit = defineEmits(['meetingClick', 'newMeetingSlot', 'meetingDrop']);

const { t } = useI18n();

const MAX_VISIBLE = 3;

const dayCellRefs = shallowRef(new Map());

function setDayCellRef(day, el) {
  const key = spDateKey(day);
  if (el) {
    dayCellRefs.value.set(key, el);
  } else {
    dayCellRefs.value.delete(key);
  }
}

const { onMeetingMousedown, dragState } = useMeetingDayDrag({
  dayCellRefs,
  emit,
});

const weeks = computed(() => {
  const year = props.currentDate.getFullYear();
  const month = props.currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const start = new Date(firstDay);
  start.setDate(start.getDate() - startOffset);

  const cells = [];
  const cursor = new Date(start);
  while (cursor <= lastDay || cells.length % 7 !== 0) {
    cells.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
    if (cells.length > 42) break;
  }

  const rows = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }
  return rows;
});

const DAY_HEADERS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function meetingsForDay(day) {
  const date = spDateKey(day);
  return props.meetings.filter(
    m => m.start_at && spDateKey(m.start_at) === date
  );
}

function isCurrentMonth(day) {
  return day.getMonth() === props.currentDate.getMonth();
}

function isToday(day) {
  return spDateKey(day) === spDateKey(new Date());
}

function onDayClick(day) {
  const start = new Date(day);
  start.setHours(9, 0, 0, 0);
  const end = new Date(start);
  end.setHours(10, 0, 0, 0);
  emit('newMeetingSlot', {
    start_at: start.toISOString(),
    end_at: end.toISOString(),
  });
}
</script>

<template>
  <div class="flex flex-col flex-1 overflow-hidden">
    <!-- Cabeçalhos dos dias da semana -->
    <div class="grid grid-cols-7 border-b border-n-weak flex-shrink-0">
      <div
        v-for="day in DAY_HEADERS"
        :key="day"
        class="py-2 text-center text-xs font-medium text-n-slate-10 uppercase"
      >
        {{ day }}
      </div>
    </div>

    <!-- Grid das semanas -->
    <div class="flex-1 grid auto-rows-fr">
      <div
        v-for="(week, wi) in weeks"
        :key="wi"
        class="grid grid-cols-7 border-b border-n-weak last:border-b-0 min-h-[100px]"
      >
        <div
          v-for="day in week"
          :key="spDateKey(day)"
          :ref="el => setDayCellRef(day, el)"
          class="border-r border-n-weak last:border-r-0 p-1 cursor-pointer hover:bg-n-alpha-1"
          :class="[
            !isCurrentMonth(day) ? 'bg-n-slate-3 text-n-slate-10' : '',
            dragState.targetDayKey === spDateKey(day) ? 'bg-n-alpha-2' : '',
          ]"
          @click="onDayClick(day)"
        >
          <span
            class="text-xs font-medium inline-flex items-center justify-center size-6 rounded-full mb-1"
            :class="
              isToday(day)
                ? 'bg-n-brand text-white'
                : isCurrentMonth(day)
                  ? 'text-n-slate-12'
                  : 'text-n-slate-10'
            "
          >
            {{ day.getDate() }}
          </span>

          <!-- Chips de reuniões -->
          <div class="flex flex-col gap-0.5">
            <div
              v-for="meeting in meetingsForDay(day).slice(0, MAX_VISIBLE)"
              :key="meeting.id"
              class="truncate rounded px-1 text-xs cursor-pointer text-white"
              :class="[
                meetingOpacityClass(meeting),
                dragState.draggingId === meeting.id ? '!opacity-30' : '',
              ]"
              :style="{ backgroundColor: meeting.user?.color }"
              @click.stop="emit('meetingClick', meeting.id)"
              @mousedown="
                onMeetingMousedown($event, meeting, $event.currentTarget)
              "
            >
              {{ meeting.title }}
            </div>
            <p
              v-if="meetingsForDay(day).length > MAX_VISIBLE"
              class="text-xs text-n-slate-10 px-1"
            >
              +{{ meetingsForDay(day).length - MAX_VISIBLE }}
              {{ t('AGENDA.MONTH_VIEW.MORE').replace('{count}', '') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
